/**
 * lib/analytics/ga4.ts — GA4 conversion events (Cadence spec 2026-10-27,
 * "Team Inbox/briefs/cadence-ga4-conversion-events-2026-10-27.md" §4.1 W1).
 *
 * Consent is inherited, never bypassed: gtag.js is only ever fetched by
 * components/ConsentManager.tsx, on the live hosts, after analytics consent.
 * app/layout.tsx always defines a queueing gtag() stub, so without consent an
 * event below only lands in window.dataLayer and is never sent.
 *
 * On any host outside GA4_LIVE_HOSTS (staging, previews, localhost) nothing
 * reaches gtag at all — the same host list ConsentManager guards with.
 *
 * Wait-for-config (2026-10-27 fix): an event or 'get' queued BEFORE
 * ConsentManager's onLoad pushes gtag('config', GA4_ID) has no target and
 * Google drops it — the membership page's mount-time begin_checkout and its
 * client/session-id reads were lost that way. Everything here therefore waits
 * until that config entry is in window.dataLayer. Without consent the config
 * never arrives, so the event is simply never queued (dropped after
 * EVENT_WAIT_MS) and the ids resolve null after IDS_WAIT_MS.
 */
import { GA4_LIVE_HOSTS } from '@/components/ConsentManager';

const GA4_ID = 'G-B1NYDDW0NP';
const GA_IDS_TIMEOUT_MS = 1000;
const POLL_MS = 100;
const EVENT_WAIT_MS = 30_000;
const IDS_WAIT_MS = 5_000;

function isLiveHost(): boolean {
  return typeof window !== 'undefined' && GA4_LIVE_HOSTS.includes(window.location.hostname);
}

function isConfigured(): boolean {
  const dl = window.dataLayer;
  if (!Array.isArray(dl)) return false;
  return dl.some((entry) => {
    const e = entry as ArrayLike<unknown> | null;
    return !!e && typeof e === 'object' && e[0] === 'config' && e[1] === GA4_ID;
  });
}

/** Resolves true once gtag('config', GA4_ID) has been queued, false after `maxMs`. */
function whenConfigured(maxMs: number): Promise<boolean> {
  return new Promise((resolve) => {
    if (isConfigured()) return resolve(true);
    const started = Date.now();
    const timer = setInterval(() => {
      if (isConfigured()) {
        clearInterval(timer);
        resolve(true);
      } else if (Date.now() - started >= maxMs) {
        clearInterval(timer);
        resolve(false);
      }
    }, POLL_MS);
  });
}

export function ga4Event(name: string, params: Record<string, unknown>): void {
  if (!isLiveHost()) return;
  void whenConfigured(EVENT_WAIT_MS).then((ready) => {
    if (ready) window.gtag?.('event', name, params);
  });
}

function gtagGet(field: 'client_id' | 'session_id'): Promise<string | null> {
  return new Promise((resolve) => {
    const timer = setTimeout(() => resolve(null), GA_IDS_TIMEOUT_MS);
    try {
      window.gtag?.('get', GA4_ID, field, (value: unknown) => {
        clearTimeout(timer);
        resolve(value === undefined || value === null || value === '' ? null : String(value));
      });
    } catch {
      clearTimeout(timer);
      resolve(null);
    }
  });
}

/**
 * The visitor's GA4 client_id + session_id, for the server-side purchase event.
 * Waits up to IDS_WAIT_MS for the config entry, then reads both ids (1,000 ms
 * timeout each). Null without consent, off the live hosts, or on timeout.
 */
export async function getGaIds(): Promise<{ cid: string; sid: string } | null> {
  if (!isLiveHost() || typeof window.gtag !== 'function') return null;
  if (!(await whenConfigured(IDS_WAIT_MS))) return null;
  const [cid, sid] = await Promise.all([gtagGet('client_id'), gtagGet('session_id')]);
  return cid && sid ? { cid, sid } : null;
}

/** The `ga` block for a checkout POST body, or null (then leave it out entirely). */
export async function gaCheckoutPayload(): Promise<GaCheckoutPayload | null> {
  const ids = await getGaIds();
  return ids ? { ...ids, page: window.location.pathname } : null;
}

export type GaCheckoutPayload = { cid: string; sid: string; page: string };
